import {
  createBoundaryObserver,
  createElementObserver,
  createMutationObserver,
  observeElement,
} from "@/vendors/Snowplow/observation/observation-boundary-observers"
import type {
  ElementObservation,
  ElementObservationsAPI,
  Observers,
} from "@/vendors/Snowplow/types/observation-boundary-tracker.types"
import { isElementInViewport } from "@/vendors/Snowplow/utilities/is-element-in-viewport"

type CreateObserverProps = {
  readonly elements: ElementObservationsAPI
  readonly onBoundaryEnter: () => void
  readonly onBoundaryExit: () => void
  readonly getIsPageVisible: () => boolean
}

/**
 * Creates the three observers needed for boundary tracking:
 * - Boundary observer: Tracks when the container enters/exits viewport
 * - Element observer: Tracks individual elements within the boundary
 * - Mutation observer: Handles dynamically added/removed elements
 *
 * @returns Object containing boundary, element, and mutation observers
 */
export function createObservers({
  elements,
  onBoundaryEnter,
  onBoundaryExit,
  getIsPageVisible,
}: CreateObserverProps) {
  const boundary = createBoundaryObserver({
    onEnter: onBoundaryEnter,
    onExit: onBoundaryExit,
  })

  const element = createElementObserver(elements, getIsPageVisible)
  const mutation = createMutationObserver(elements, () => ({ element }))

  return { boundary, element, mutation }
}

/**
 * Attaches all observers to the boundary element and starts tracking
 * any existing `[data-obs-element]` children.
 *
 * Call this after creating observers to begin observation.
 *
 * @param boundary - The container element defining the tracking scope
 * @param observers - The observer instances from createObservers
 * @param elements - The element observations API for tracking state
 */
export function setupObservers(
  boundary: HTMLElement,
  observers: Observers,
  elements: ElementObservationsAPI,
): void {
  observers.boundary.observe(boundary)
  observers.mutation.observe(boundary, { childList: true, subtree: true })

  const observedElements =
    boundary.querySelectorAll<HTMLElement>("[data-obs-element]")

  for (const element of observedElements) {
    observeElement(element, elements, observers.element)
  }
}

/**
 * Disconnects all three observers, stopping all tracking.
 *
 * Call this during cleanup before clearing element data.
 */
export function disconnectObservers(observers: Observers): void {
  observers.boundary.disconnect()
  observers.element.disconnect()
  observers.mutation.disconnect()
}

/**
 * Re-scans the boundary and starts tracking any elements that are
 * currently visible in the viewport but not yet being tracked.
 *
 * Used when the page becomes visible again after being hidden,
 * to resume tracking for elements that were in view.
 *
 * @param boundary - The container element to scan
 * @param elements - The element observations API
 * @param now - Current timestamp for tracking
 * @param mark - Current performance.now() mark for duration calculation
 */
export function startTrackingVisibleElements(
  boundary: HTMLElement,
  elements: ElementObservationsAPI,
  now: Date,
  mark: number,
): void {
  const observedElements =
    boundary.querySelectorAll<HTMLElement>("[data-obs-element]")

  for (const element of observedElements) {
    const observation = elements.getElement(element)

    if (!observation || observation.isCurrentlyVisible) {
      continue
    }

    const rect = element.getBoundingClientRect()

    if (isElementInViewport(rect)) {
      elements.startTracking(element, now, mark)
    }
  }
}

/**
 * Creates an ElementObservation record from an HTML element's data attributes.
 *
 * Reads:
 * - `data-obs-element-id` - Required unique identifier
 * - `data-obs-element-content` - Required content identifier for analytics
 * - `data-obs-element-type` - Optional element type category
 *
 * @param element - The HTML element with data attributes
 * @returns ElementObservation object or null if required attributes are missing
 */
export function createElementObservation(
  element: HTMLElement,
): ElementObservation | null {
  const elementId = element.getAttribute("data-obs-element-id")
  const content = element.getAttribute("data-obs-element-content")
  const elementType = element.getAttribute("data-obs-element-type")

  if (!elementId || !content) {
    return null
  }

  return {
    content,
    elementId,
    elementType,
    firstViewTimestamp: null,
    isCurrentlyVisible: false,
    lastViewTimestamp: null,
    maxElementHeight: 0,
    maxElementWidth: 0,
    maxPixelsInView: 0,
    timeInViewMs: 0,
    viewCount: 0,
    visibilityStartMark: null,
  }
}
